// Compatibility shim so ported messaging-front pages keep importing
// `{ useAuth }` from '../contexts/AuthContext'. It wraps the chatbot_sso React
// SDK and exposes the fields those pages use: `getToken()` (fresh SSO access
// token), plus `can`/`userInfo`/`token` conveniences. Actual auth + the
// authenticated fetch live in the SDK AuthProvider (mounted in main.tsx) and
// the authBridge; ported pages still pass `getToken()` into hub.service calls,
// but the transport ignores it and uses the SSO apiFetch regardless.
//
// IMPORTANT: returns are memoized with stable identities — several ported pages
// put `getToken`/the auth object in useEffect deps; fresh refs each render would
// cause infinite update loops.
import { useCallback, useMemo } from 'react';
import { useAuth as useSdkAuth } from '@next-social-platform/chatbot_sso/react';

interface HubUser {
  name?: string;
  email?: string;
  username?: string;
  roles?: unknown;
}

export function useAuth() {
  const a = useSdkAuth<HubUser>();
  const getAccessToken = a.getAccessToken;
  const authenticated = a.authenticated;

  const getToken = useCallback((): string | null => getAccessToken?.() ?? null, [getAccessToken]);
  const can = useCallback((_opts?: { type: string }) => authenticated, [authenticated]);
  const userInfo = useCallback(
    () => ({ name: a.user?.name ?? a.user?.username ?? a.user?.email ?? 'User', email: a.user?.email }),
    [a.user],
  );

  return useMemo(
    () => ({ ...a, token: getToken(), getToken, isOAuth: authenticated, can, userInfo }),
    [a, getToken, authenticated, can, userInfo],
  );
}
